<template>
  <page-wrapper
    error-key="quotaOrders"
    :sticky-second-row="!$vuetify.display.mdAndDown.value"
    :transparent-bg="hideFilters"
  >
    <template #title>
      <div class="h3">Бронирования</div>
    </template>

    <template v-if="!hideFilters" #secondRow>
      <books-filters :filters="filters" />
    </template>

    <template v-if="booksTableItems.length || isLoading">
      <div class="paragraph-s books__count">
        Найдено бронирований:
        <b class="ml-1 paragraph-m_h">
          {{ booksTableItems.length }}
        </b>
      </div>

      <e-data-table
        v-if="$vuetify.display.mdAndUp.value"
        interactive
        :items="booksTableItems"
        :headers="tableHeaders"
        :loading="isLoading"
        :sticky-header-top="$vuetify.display.mdAndDown.value ? '0' : '144px'"
        @on-row-click="rowClickHandler"
      >
        <template v-slot:[`item.number`]="{ value, item }">
          <span :class="{ 'with-badge': !item.sentToSupplier }">{{ value }}</span>
        </template>
        <template v-slot:[`item.roomCategory`]="{ value }">
          <span class="text-ellipsis">{{ value }}</span>
        </template>
        <template v-slot:[`item.tourists`]="{ value }">
          {{ value.length }}
          <help-tooltip>
            <div v-for="(item, index) in value" :key="index">
              {{ `${item.touristType}, ${item.name}` }}
            </div>
          </help-tooltip>
        </template>
        <template v-slot:[`item.status`]="{ value }">
          <book-status :status="value" />
        </template>
      </e-data-table>

      <div v-else class="mt-4">
        <template v-if="isLoading">
          <skeleton-loader v-for="item in 10" height="180px" class="mb-4" :key="item" />
        </template>
        <template v-else>
          <div
            v-for="item in booksTableItems"
            :key="item.id"
            class="books__item"
            @click="rowClickHandler($event, { item })"
          >
            <div class="books__item-row">
              <b class="paragraph-s text-secondary" :class="{ 'with-badge-before': !item.sentToSupplier }">
                {{ item.bookDate }}
              </b>
              <book-status class="paragraph-s" :status="item.status" />
            </div>
            <div class="books__item-row align-start mt-2">
              <b class="paragraph-l">
                {{ item.roomCategory }}
              </b>
              <span>
                {{ item.number }}
              </span>
            </div>

            <div class="books__item-row justify-start paragraph-s text-secondary mt-1">
              <e-icon class="mr-1" name="calendarTick" />
              <b>
                {{ item.residenceDateFrom }} -
                {{ item.residenceDateTo }}
              </b>

              <vertical-line class="mx-2" />

              <e-icon class="mr-1" name="userMultiple" />
              {{ item.tourists.length }}
            </div>
          </div>
        </template>

        <books-filters-dialog v-model="filters" />
      </div>
    </template>

    <div v-else class="books__empty">
      <img width="160" src="../../../assets/img/pages/books/no-data-image.svg" class="mb-4" />
      <div class="books__empty-text paragraph-l">
        <b v-if="!books.loaded">Выберите Приложение для получения списка бронирований</b>
        <b v-else>Бронирований не найдено. Попробуйте изменить параметры фильтра</b>
      </div>
    </div>
  </page-wrapper>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import type { OrderStatus, PlaceType, QuotaOrdersQueryVariables } from '@/graphql/generated';
import VerticalLine from '@/core/components/helpers/VerticalLine.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { EDataTable, EIcon, HelpTooltip } from '@/core/components/ui';
import { useServices } from '@/core/composables/services';
import { useFetch } from '@/core/composables/useFetch';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { getIsoDateFormat, isLessDate, isMoreDate, stringToLocaleDate } from '@/core/utils/date';
import { useComputedValue } from '@/core/utils/reactivity';
import {
  booleanUrlSerializer,
  dateArrayUrlSerializer,
  numberUrlSerializer,
  stringUrlSerializer,
  useQueryValue,
} from '@/core/utils/router/query-value';
import BookStatus from '@/parts/books/components/BookStatus.vue';
import BooksFilters from '@/parts/books/components/BooksFilters.vue';
import BooksFiltersDialog from '@/parts/books/components/BooksFiltersDialog.vue';
import { useBooksStore } from '@/parts/books/store/books';
import { type BooksFilters as BooksFiltersType, defaultFilters } from '@/parts/books/utils/consts';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import { useOpenInNewTab } from '@/core/composables/router';

const { $vuetify } = useServices();

const { getBooks, books, resetBooks } = useBooksStore();
const $router = useRouter();
const openInNewTab = useOpenInNewTab();
const { accommodationObject, activeAccount } = useAccommodationObjectsStore();

const filters = ref<BooksFiltersType>(defaultFilters(activeAccount.value?.contractNumber));

useQueryValue(useComputedValue(filters, 'bookDate'), 'bookDate', dateArrayUrlSerializer);
useQueryValue(useComputedValue(filters, 'residenceDate'), 'residenceDate', dateArrayUrlSerializer);
useQueryValue(useComputedValue(filters, 'account'), 'account', numberUrlSerializer);
useQueryValue(useComputedValue(filters, 'tariff'), 'tariff', stringUrlSerializer);
useQueryValue(useComputedValue(filters, 'roomCategory'), 'roomCategory', stringUrlSerializer);
useQueryValue(useComputedValue(filters, 'status'), 'status', stringUrlSerializer);
useQueryValue(useComputedValue(filters, 'checkRequired'), 'checkRequired', booleanUrlSerializer);

const loadBooks = async () => {
  if (!filters.value.residenceDate[0]) {
    resetBooks();
    return;
  }

  const payload: QuotaOrdersQueryVariables = {
    dateFrom: getIsoDateFormat(filters.value.residenceDate[0]),
    dateTo: filters.value.residenceDate[1] ? getIsoDateFormat(filters.value.residenceDate[1]) : null,
  };

  if (filters.value.tariff) {
    payload.tariffScheduleCid = filters.value.tariff;
  } else if (filters.value.account) {
    payload.contractNumber = filters.value.account;
  } else {
    payload.accommodationObjectCid = accommodationObject.value?.cid;
  }

  await getBooks(payload);
};

watch([() => filters.value.residenceDate, () => filters.value.account, () => filters.value.tariff], loadBooks, {
  deep: true,
});

const { isLoading } = useFetch(loadBooks);

watch(
  () => filters.value.account,
  () => {
    filters.value.tariff = null;
  },
);

type BookTableItem = {
  id: number;
  number: string;
  bookDate: string;
  roomCategory: string;
  tourists: { name: string; touristType: string; placeType: PlaceType }[];
  residenceDateFrom: string;
  residenceDateTo: string;
  status: OrderStatus;
  sentToSupplier: boolean;
};

const booksTableItems = computed<BookTableItem[]>(() => {
  if (!books.value.loaded) {
    return [];
  }

  return books.value.data.quotaOrders
    .filter((item) => {
      if (filters.value.bookDate[0] && isMoreDate(filters.value.bookDate[0], item.orderDate)) {
        return false;
      }

      const bookDateEnd = filters.value.bookDate.at(-1);

      if (filters.value.bookDate[1] && bookDateEnd && isLessDate(bookDateEnd, item.orderDate)) {
        return false;
      }

      if (filters.value.roomCategory && item.roomCategory.cid !== filters.value.roomCategory) {
        return false;
      }

      if (filters.value.status && item.status !== filters.value.status) {
        return false;
      }

      if (filters.value.checkRequired && item.sentToSupplier) {
        return false;
      }

      return true;
    })
    .map((item) => ({
      id: item.orderId,
      number: `${item.orderNumber}/${item.subOrderNumber}`,
      bookDate: stringToLocaleDate(item.orderDate),
      roomCategory: item.roomCategory.name[0].value,
      tourists: item.tourists,
      residenceDateFrom: stringToLocaleDate(item.period.from),
      residenceDateTo: stringToLocaleDate(item.period.to),
      status: item.status,
      sentToSupplier: item.sentToSupplier,
    }));
});

const hideFilters = toRef(() => !!booksTableItems.value.length && !$vuetify.display.mdAndUp.value);

const tableHeaders = computed(() => [
  { title: 'Номер', key: 'number', width: '120px' },
  {
    title: 'Дата бронирования',
    key: 'bookDate',
    width: $vuetify.display.mdAndDown.value ? '124px' : '186px',
  },
  { title: 'Категория номера', key: 'roomCategory' },
  { title: 'Туристы', key: 'tourists', width: '112px' },
  {
    title: 'Дата заезда',
    key: 'residenceDateFrom',
    width: $vuetify.display.mdAndDown.value ? '124px' : '140px',
  },
  {
    title: 'Дата выезда',
    key: 'residenceDateTo',
    width: $vuetify.display.mdAndDown.value ? '124px' : '140px',
  },
  { title: 'Статус', key: 'status', width: $vuetify.display.mdAndDown.value ? '146px' : '240px' },
]);

const rowClickHandler = (e: Event, row: { item: BookTableItem }) => {
  const { id } = row.item;
  openInNewTab(RouteNames.Book, { b_id: id });
};
</script>

<style scoped lang="scss">
.books {
  &__count {
    margin-top: 8px;
    padding: 0 16px;

    @include md-and-down {
      margin-top: 0;
      padding: 0;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;

    margin-top: 133px;

    @include xs {
      margin-top: 52px;
    }

    @include extra-xs {
      margin-top: 36px;
    }

    &-text {
      max-width: 560px;
      margin-top: 8px;

      text-align: center;
      color: $text-ordinary-color;

      @include xs {
        max-width: 450px;
      }

      @include extra-xs {
        max-width: 250px;
      }
    }
  }

  &__item {
    margin-bottom: 16px;
    padding: 16px;

    background-color: $main-bg-color;
    border-radius: $border-radius-l;

    &-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
}

:deep(.v-data-table table) {
  table-layout: fixed;
}
</style>
